/**
 * Password Validation Utilities
 * Based on FRONTEND_AUTH_GUIDE.md requirements
 */

export interface PasswordValidation {
  isValid: boolean;
  errors: string[];
}

export interface PasswordStrength {
  score: number;        // 0-4
  label: string;        // 'Very Weak', 'Weak', 'Fair', 'Good', 'Strong'
  color: string;        // CSS color
  percentage: number;   // 0-100 for progress bar
}

/**
 * Validate password against requirements
 * 
 * Requirements (ALL REQUIRED):
 * - Minimum 8 characters
 * - At least one uppercase letter (A-Z)
 * - At least one lowercase letter (a-z)
 * - At least one number (0-9)
 */
export function validatePassword(password: string): PasswordValidation {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters');
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Check password strength for visual indicator
 * Returns a score from 0-4 with label and color
 */
export function checkPasswordStrength(password: string): PasswordStrength {
  let score = 0;
  
  // Length
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  
  // Character types
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++; // Special chars (bonus)
  
  // Normalize to 0-4
  score = Math.min(4, Math.floor(score / 1.5));
  
  const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  const colors = ['#ff4444', '#ff8800', '#ffbb00', '#88cc00', '#00cc44'];
  
  return {
    score,
    label: labels[score],
    color: colors[score],
    percentage: (score / 4) * 100,
  };
}

